// Plan on a phone (PhonePlanPage on the Mac's iPhone): the folder's items as a column of cards (the point of view's
// colour down the edge) or its whole outline, with "Cards · Outline · Beats" to switch. A folder opens in place; a
// scene opens in Write. "‹ Parent" goes back up. A character's study page shows the character: their colour, role and
// scenes, not the page's boilerplate.
import { entityCSSColor, JournalPosts, journalPostsInTree, type TreeNode } from '@writing-suite/core';
import { dayName, postColumns, PUBLISHED_SHOWN } from './PostBoard.tsx';
import { isJournalPosts, plannedPost, PostHeader, PostPlanEmpty, withoutSections } from './posts.tsx';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { followLink } from '../../shell/route.ts';
import { boardFor, flattenTree, formatNumber, hasChildren, pathTo } from '../helpers.ts';
import type { PlanHeat } from './hooks.ts';
import { cardCharacter, cardFacts, castAppearances, heatClass } from './IndexCard.tsx';

export function PhonePlan(props: PageProps & { outline: boolean; heat?: PlanHeat }) {
  const { project, tree, node, arrangement, hrefFor, outline, heat } = props;
  const posts = isJournalPosts(project, arrangement);
  const planned = posts ? plannedPost(tree, node?.id ?? null) : null;
  const postIds = posts ? new Set(journalPostsInTree(tree).map((p) => p.nodeId)) : null;
  const board = boardFor(tree, node?.id ?? null, posts);
  const container = board.folder;
  const path = container ? pathTo(tree, container.id) : null;
  const parent = path && path.length > 1 ? path[path.length - 2] : null;
  const fields = project.fields();
  const entities = project.entities();
  const title = container ? container.title || 'Untitled' : arrangement.name;
  const page = outline ? 'plan/outliner' : 'plan/corkboard';
  const backHref = hrefFor(page, parent?.id ?? null);
  const nodes = board.cards;
  const link = (href: string, label: string, current: boolean) => (
    <a href={href} aria-current={current ? 'page' : undefined} onClick={(e) => followLink(e, href)}>
      {label}
    </a>
  );
  // A journal's posts by status, as the Board has them (Published the newest 12).
  const byStatus = posts && !planned && !outline ? postColumns(project, journalPostsInTree(board.folder ? board.cards : tree)) : null;
  const phoneCard = (n: TreeNode) => {
    const f = cardFacts(project, n, fields, entities);
    const isPost = !!postIds?.has(n.id);
    const folder = isPost || hasChildren(n);
    const href = folder ? hrefFor('plan/corkboard', n.id) : hrefFor('write', n.id);
    const character = cardCharacter(project, n);
    const scenes = character ? castAppearances(project, character).scenes : 0;
    const postFoot = isPost && n.fragmentId ? (() => {
      const meta = project.fragment(n.fragmentId)?.metadata;
      const number = JournalPosts.number(meta);
      const date = meta?.['publish-date']?.type === 'date' ? meta['publish-date'].value : null;
      const scheduled = meta?.status?.type === 'text' && meta.status.value === JournalPosts.scheduled;
      return [number !== null ? `#${number}` : null, date ? dayName(date) : scheduled ? 'No date' : null].filter(Boolean).join(' · ');
    })() : null;
    const foot = character
      ? scenes === 0 ? 'Not in the manuscript yet' : scenes === 1 ? '1 scene' : `${formatNumber(scenes)} scenes`
      : postFoot ?? [f.povName, f.status].filter(Boolean).join(' · ');
    const edge = character ? entityCSSColor(character.color) : f.edge;
    const synopsis = character ? (character.role ?? '') : (f.fragment?.synopsis ?? '');
    const sections = isPost ? n.children.length : 0;
    return (
      <li key={n.id} data-node-id={n.id} className={`phone-card${heatClass(heat?.heat[n.id])}`} data-cast-card={character?.id}>
        <a href={href} onClick={(e) => followLink(e, href)} title={isPost ? 'Shows its plan' : folder ? 'Shows what’s in it' : 'Opens in Write'}>
          <span className="phone-card-edge" aria-hidden="true" ref={(el) => void (edge ? el?.style.setProperty('--edge', edge) : el?.style.removeProperty('--edge'))} />
          <span className="phone-card-body">
            <span className="phone-card-top">
              <span className="card-title">{n.title || 'Untitled'}</span>
              <span className="card-words">{formatNumber(f.words)}</span>
            </span>
            {synopsis.trim() ? <span className="card-synopsis">{synopsis}</span> : null}
            {isPost ? (
              <span className="card-foot">{[foot, sections ? (sections === 1 ? '1 section' : `${sections} sections`) : null].filter(Boolean).join(' · ')}</span>
            ) : folder ? (
              <span className="card-foot">{n.children.length === 1 ? '1 item' : `${n.children.length} items`}</span>
            ) : foot ? (
              <span className="card-foot">{foot}</span>
            ) : null}
          </span>
        </a>
      </li>
    );
  };
  const nodeOf = (id: string) => pathTo(tree, id)?.at(-1) ?? null;
  return (
    <div className="phone-plan" data-phone-plan={outline ? 'outline' : 'cards'}>
      {planned ? <PostHeader {...props} post={planned.post} node={planned.node} /> : null}
      <header className={planned ? 'phone-plan-head post-plan-switch' : 'phone-plan-head'}>
        {container && !planned ? (
          <a className="phone-plan-back" href={backHref} onClick={(e) => followLink(e, backHref)}>
            ‹ {parent ? parent.title || 'Untitled' : arrangement.name}
          </a>
        ) : null}
        <div className="phone-plan-title">
          {planned ? null : <h1 tabIndex={-1}>{title}</h1>}
          <nav className="phone-plan-switch" aria-label="Plan views">
            {link(hrefFor('plan/corkboard', node?.id ?? null), 'Cards', !outline)}
            <span aria-hidden="true">·</span>
            {link(hrefFor('plan/outliner', node?.id ?? null), 'Outline', outline)}
            {posts ? null : <span aria-hidden="true">·</span>}
            {posts ? null : link(hrefFor('plan/beats', null), 'Beats', false)}
          </nav>
        </div>
      </header>
      {nodes.length === 0 ? (planned ? <PostPlanEmpty post={planned.post} /> : <p className="empty-note">Nothing here yet.</p>) : null}
      {outline ? (
        <ul className="phone-outline">
          {flattenTree(posts && !planned ? withoutSections(nodes) : nodes).map(({ node: n, depth }) => {
            const f = cardFacts(project, n, fields, entities);
            const href = hrefFor('write', n.id);
            return (
              <li key={n.id} data-node-id={n.id} className={`depth-${Math.min(depth, 6)}`}>
                <a href={href} onClick={(e) => followLink(e, href)}>
                  <span className={depth === 0 ? 'phone-outline-title top' : 'phone-outline-title'}>{n.title || 'Untitled'}</span>
                  {f.status ? <span className="phone-outline-status">{f.status}</span> : null}
                  <span className="card-words">{formatNumber(f.words)}</span>
                </a>
              </li>
            );
          })}
        </ul>
      ) : (
        byStatus ? (
          <div className="phone-posts">
            {byStatus.map((column) => {
              const shown = column.id === JournalPosts.published ? column.posts.slice(0, PUBLISHED_SHOWN) : column.posts;
              return (
                <section key={column.id || 'none'} className="phone-post-status" data-status={column.id || 'none'}>
                  <h2 className="post-column-title">
                    <span>{column.name}</span>
                    <span className="post-column-count">{formatNumber(column.posts.length)}</span>
                  </h2>
                  <ul className="phone-cards">{shown.map((p) => nodeOf(p.nodeId)).filter((n): n is TreeNode => !!n).map(phoneCard)}</ul>
                </section>
              );
            })}
          </div>
        ) : (
          <ul className="phone-cards">{nodes.map(phoneCard)}</ul>
        )
      )}
    </div>
  );
}
